import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import data from "./data.json";
import { useDispatch, useSelector } from "react-redux";
import {
  setExternalLink,
  setShowExternalPopup,
} from "../../../redux/slice/slicePopup";
import { useGetProtectedApiDataQuery } from "../../../redux/services/apiSlice";
import MenuSkeleton from "../MenuSkeleton";

const Menus = () => {
  const POWER_GENERATION_URL = "/power-generation";
  const { pathname } = useLocation();
  const subMenuRef = useRef({});
  const [toggleShowClass, setToggleShowClass] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const dispatch = useDispatch();
  const { languageType } = useSelector((state) => state.homeSlice);
  const isMobile = screenWidth <= 738;

  const {
    data: mainMenus = {},
    isLoading,
    isFetching,
  } = useGetProtectedApiDataQuery({
    url: "mastermenu",
    lang: languageType,
  });

  const showLoader = isLoading || isFetching;

  const menuItems = useMemo(() => {
    if (mainMenus && Array.isArray(mainMenus.menu_items)) {
      return mainMenus.menu_items;
    }
    return [];
  }, [mainMenus]);

  const handlePopupLink = useCallback(
    (link) => {
      dispatch(setShowExternalPopup(true));
      dispatch(setExternalLink(link));
    },
    [dispatch],
  );

  const isActive = useCallback(
    (path) => {
      if (path === "/") {
        return pathname === "/" ? "active" : ""; // Exact match for the root path
      }
      return pathname.includes(path) ? "active" : ""; // Partial match for other paths
    },
    [pathname],
  );

  const getActiveClass = useCallback(
    (path) => (isActive(path) ? "active" : ""),
    [isActive],
  );

  const handleMobileMenu = useCallback(() => {
    setToggleShowClass((s) => !s);
  }, []);

  const handleSubMenu = (menu, event) => {
    if (event && event.type === "click") {
      const isPowerGeneration = menu.url === POWER_GENERATION_URL;
      if (isPowerGeneration) {
        setActiveSubMenu(null);
        return;
      }
    }
    setActiveSubMenu((prev) => (prev === menu.title ? null : menu.title));
  };

  const closeMenuOnItemClick = useCallback(() => {
    setToggleShowClass(false);
    setActiveSubMenu(null);
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        setActiveSubMenu(null);
        setToggleShowClass(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  // Render menu item
  const renderMenuItem = (menu, index) => {
    const hasSubmenu = menu?.submenu?.length > 0;
    const isPowerGeneration = menu.url === POWER_GENERATION_URL;
    const isActiveSubMenu = activeSubMenu === menu.title;

    return (
      <li key={index} className={`nav-item ${hasSubmenu ? "menuparent" : ""}`}>
        {hasSubmenu && isPowerGeneration ? (
          // Power Generation menu with special handling
          <Link
            role="button"
            to={menu.url}
            className={`parent-menu ${getActiveClass(menu.url)} ${
              isActiveSubMenu ? "show-submenu" : ""
            }`}
            onClick={(e) => {
              if (isMobile) {
                e.preventDefault();
                setActiveSubMenu((prev) =>
                  prev === menu.title ? null : menu.title,
                );
              } else {
                setActiveSubMenu(null);
                setToggleShowClass(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSubMenu(menu, e);
              }
            }}
          >
            {menu.title}
          </Link>
        ) : hasSubmenu ? (
          // Regular menu with submenu
          <a
            role="button"
            href="#"
            className={`parent-menu ${getActiveClass(menu.url)} ${
              isActiveSubMenu ? "show-submenu" : ""
            }`}
            onClick={(e) => {
              e.preventDefault();
              handleSubMenu(menu);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSubMenu(menu, e);
              }
            }}
          >
            {menu.title}
          </a>
        ) : (
          // Simple menu item without submenu
          <Link
            to={menu.url}
            className={`parent-menu ${getActiveClass(menu.url)}`}
            onClick={closeMenuOnItemClick}
          >
            {menu.title}
          </Link>
        )}

        {hasSubmenu && renderSubMenu(menu)}
      </li>
    );
  };

  // Render submenu
  const renderSubMenu = (menu) => {
    const shouldHidePowerGeneration = (submenu) =>
      submenu.url === POWER_GENERATION_URL && screenWidth > 738;

    return (
      <ul
        className="sub-menu"
        ref={(el) => (subMenuRef.current[menu.title] = el)}
      >
        {menu.submenu.map((submenu, subIndex) =>
          shouldHidePowerGeneration(submenu) ? null : (
            <li key={subIndex} className="item-list">
              {submenu.external ? (
                <a
                  href="#"
                  title={submenu.title}
                  onClick={(e) => {
                    e.preventDefault();
                    handlePopupLink(submenu.url);
                  }}
                  rel="noopener noreferrer"
                >
                  {submenu.title}
                </a>
              ) : (
                <Link
                  title={submenu.title}
                  to={submenu.url}
                  className={getActiveClass(submenu.url)}
                  onClick={closeMenuOnItemClick}
                >
                  {submenu.title}
                </Link>
              )}
            </li>
          ),
        )}
      </ul>
    );
  };

  useEffect(() => {
    const onResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      <div className="nav-wraper">
        <nav className="navbar navbar-expand-lg navbar-light custom-nav w-100">
          <button
            className="navbar-toggler"
            type="button"
            onClick={handleMobileMenu}
          >
            {!toggleShowClass ? (
              <i className="fas fa-bars"></i>
            ) : (
              <i className="fas fa-times"></i>
            )}
          </button>
          <div
            className={`collapse navbar-collapse ${
              toggleShowClass ? "show" : ""
            }`}
            id="navbarSupportedContent"
          >
            <ul className="navbar-nav mr-auto">
              {showLoader ? (
                <MenuSkeleton count={7} />
              ) : menuItems.length > 0 ? (
                menuItems.map(renderMenuItem)
              ) : (
                <li>No Menu Found</li>
              )}
            </ul>
            ;
          </div>
        </nav>
      </div>
    </>
  );
};

export default Menus;

{
  /* <ul className="navbar-nav mr-auto">
  {showLoader ? (
    <MenuSkeleton count={7} />
  ) : menuItems.length > 0 ? (
    menuItems.map(renderMenuItem)
  ) : (
    <li>No Menu Found</li>
  )}
</ul>; */
}
